import { useState } from "react";
import { ErrorType } from "@components/global/new/Error/Error";
import { EditUserInfoRequest, EditUserParams } from "@services/editUser";
import { useMe } from "@utils/hooks/useMe";
import { Me } from "@store/me/reducer";
import {
  Create2FaRequest,
  ICreateTwoFA,
  Create_2faResponseI,
} from "@services/twoFa";
import { useDispatch } from "react-redux";
import { show } from "redux-modal";
import { MODALS_ENUM } from "@constants/Modals";
import {
  IAccount,
  NotificationsEmails,
} from "@services/http/responses/AccountResponse";
import useApiRequests from "@services/http/useApiRequest";
import { makeNotifs } from "@utils/makeNotifs";
import { useToasts } from "@utils/hooks/useToasts";
interface Elems extends HTMLFormControlsCollection {
  [key: string]: any;
}

interface MyForm extends HTMLFormElement {
  readonly elements: Elems;
}

interface EditAccountControlI {
  readonly me?: Me;
  readonly notifsEmails: string[];
  readonly userImage?: string;
  readonly maxEmails: boolean;
  readonly errors?: ErrorType;
  readonly changePassErr?: ErrorType;
  readonly isLoading: boolean;
  readonly privateEmail: boolean;
  readonly privateTelegram: boolean;
  readonly summaryEmail: boolean;
  readonly summaryTelegram: boolean;
  readonly twoFa: boolean;

  addEmail(): void;
  onSave(e: React.FormEvent<MyForm>): void;
  getImageUrl(f: File | string): void;
  deleteEmail(i: number): void;
  onEnterEmail(email: string, i: number): void;
  setPrivateEmail(p: boolean): void;
  setPrivateTelegram(p: boolean): void;
  setSummaryEmail(s: boolean): void;
  setSummaryTelegram(s: boolean): void;
  onChangeTwoFa(): void;
}

export const getNotifEmailsString = (
  emails: NotificationsEmails[]
): string[] => {
  const arr: string[] = [];
  emails.map((e) => arr.push(e.email));
  return arr;
};

export const useEditAccountControl = (
  user: IAccount,
  update: () => void
): EditAccountControlI => {
  const me = useMe();
  const dispatch = useDispatch();
  const { info, success } = useToasts();
  const { ok } = makeNotifs();
  const [notifsEmails, setNotifsEmails] = useState<string[]>(
    getNotifEmailsString(user.notification_emails)
  );
  const requests = useApiRequests();
  const [userImage, setUserImage] = useState<string | undefined>(user.avatar);
  const [imgFile, setImgFile] = useState<File>();
  const [errors, setErrors] = useState<ErrorType>();
  const [changePassErr, setChangePassError] = useState<ErrorType>();
  // const [isLoading, setIsLoading] = useState<boolean>(false);
  const [privateEmail, setPrivateEmail] = useState<boolean>(
    user.email.is_private
  );
  const [privateTelegram, setPrivateTelegram] = useState<boolean>(
    user.telegram.is_private
  );
  const [summaryEmail, setSummaryEmail] = useState<boolean>(user.summary_email);
  const [summaryTelegram, setSummaryTelegram] = useState<boolean>(
    user.summary_telegram
  );
  const [twoFa, setTwoFa] = useState<boolean>(user.twofa || false);
  const maxEmails = notifsEmails.length === 5;

  const getImageUrl = (file: File) => {
    setUserImage(URL.createObjectURL(file));
    setImgFile(file);
  };

  const addEmail = () => setNotifsEmails([...notifsEmails, ""]);

  const deleteEmail = (i: number) => {
    const newArr = notifsEmails.filter((item, index) => index !== i);
    setNotifsEmails(newArr);
  };

  const onEnterEmail = (email: string, i: number) => {
    setNotifsEmails([
      ...notifsEmails.slice(0, i),
      email,
      ...notifsEmails.slice(i + 1),
    ]);
  };

  const onSave = async (e: React.FormEvent<MyForm>) => {
    e.preventDefault();
    const elems = e.currentTarget.elements;
    const pass = elems.namedItem("password") as HTMLInputElement;
    const repeat = elems.namedItem("repeat_password") as HTMLInputElement;
    const entered_password = elems.namedItem(
      "entered_password"
    ) as HTMLInputElement;

    let values: EditUserParams = {};
    for (let i = 0; i <= elems.length; i++) {
      const element = elems[i] as HTMLInputElement;
      if (element && !!element.id) {
        if (element.id.includes("email_")) {
          values = {
            ...values,
            notification_emails: notifsEmails.toString(),
          };
        } else if (!element.id.includes("upload")) {
          let err: ErrorType | undefined = undefined;
          let changePasswordErr: ErrorType | undefined = undefined;
          // проверка на введение пароля для сохранения
          if (!entered_password.value) err = { password: true };
          if (entered_password.value.length < 6) err = { ...err, length: true };
          // проверка для изменения пароля профиля
          if (pass.value !== repeat.value) changePasswordErr = { match: true };
          if (
            !!pass.value &&
            !!repeat.value &&
            (pass.value.length < 6 || repeat.value.length < 6)
          )
            changePasswordErr = { ...changePasswordErr, length: true };

          setErrors(err);
          setChangePassError(changePasswordErr);
          if (err || changePasswordErr) return;

          values = {
            ...values,
            [element.id]: element.value,
          };
        }
      }
    }

    if (imgFile)
      values = {
        ...values,
        avatar: imgFile,
      };

    const data = await requests.enqueue(
      new EditUserInfoRequest({
        ...values,
        private_email: privateEmail,
        private_telegram: privateTelegram,
        summary_email: summaryEmail,
        summary_telegram: summaryTelegram,
        twofa: twoFa,
      })
    );

    if (!data) return;
    update();
    //Crate 2FA
    const fa = await checkTwoFa();
    if (fa) {
      dispatch(show(MODALS_ENUM.CREATE_TWO_FA_MODAL, { fa }));
    }
    if (values.email !== user.email.value) {
      info(ok.check_email);
    } else {
      success(ok.success);
    }
  };

  const checkTwoFa = (): Promise<ICreateTwoFA | false> => {
    const check: Promise<ICreateTwoFA | false> = new Promise(async (res) => {
      if (!twoFa || user.twofa) return res(false);
      const faResponse = await requests.enqueue<Create_2faResponseI>(
        new Create2FaRequest()
      );
      if (!faResponse) return res(false);
      res({ Qr: faResponse.Qr, Resetcode: faResponse.Resetcode });
    });
    return check;
  };

  const onChangeTwoFa = () => setTwoFa((prev) => !prev);

  return {
    me,
    notifsEmails,
    userImage,
    maxEmails,
    errors,
    changePassErr,
    isLoading: requests.isLoading,
    privateEmail,
    privateTelegram,
    summaryEmail,
    summaryTelegram,
    twoFa,

    addEmail,
    onSave,
    getImageUrl,
    deleteEmail,
    onEnterEmail,
    setPrivateEmail,
    setPrivateTelegram,
    setSummaryEmail,
    setSummaryTelegram,
    onChangeTwoFa,
  };
};
